import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crop, ExternalLink, ImagePlus, Loader2, LogOut, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import UploadButton from "../../components/admin/UploadButton";
import CropDialog from "../../components/admin/CropDialog";
import { Slider } from "../../components/ui/slider";
import { PAW_PRESETS, effectivePawColor, normalizePawColor, pawStyle } from "../../lib/paws";
import BookingsTab from "./tabs/BookingsTab";
import WaiversTab from "./tabs/WaiversTab";
import ReviewsTab from "./tabs/ReviewsTab";
import TransformationsTab from "./tabs/TransformationsTab";
import HolidayCard from "./tabs/HolidayCard";
import ExitPopupCard from "./tabs/ExitPopupCard";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

function SaveBar({ section, saving, onSave }) {
  return (
    <div className="mt-8 flex justify-end">
      <Button
        onClick={() => onSave(section)}
        disabled={saving !== ""}
        data-testid={`save-${section}-button`}
        className="rounded-full bg-lavender-700 px-7 font-semibold hover:bg-lavender-800"
      >
        {saving === section ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} className="mr-2" />}
        Save {section}
      </Button>
    </div>
  );
}

function Card({ children, onDelete, testid }) {
  return (
    <div data-testid={testid} className="relative rounded-3xl border border-lavender-700/10 bg-white p-6 shadow-soft">
      {onDelete && (
        <button
          onClick={onDelete}
          data-testid={`${testid}-delete`}
          aria-label="Delete"
          className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100"
        >
          <Trash2 size={14} />
        </button>
      )}
      {children}
    </div>
  );
}

const fieldCls = "mt-1.5";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState("");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [newBookings, setNewBookings] = useState(0);
  const [cropping, setCropping] = useState(null);

  useEffect(() => {
    api.get("/content")
      .then((r) => setContent({ testimonials: [], transformations: [], ...r.data }))
      .catch(() => toast.error("Could not load site content"));
    api.get("/admin/bookings")
      .then((r) => setNewBookings(r.data.filter((b) => b.status === "new").length))
      .catch(() => {});
  }, []);

  const save = async (section) => {
    setSaving(section);
    try {
      await api.put("/admin/content", content);
      toast.success("Saved — it's live on the site now.");
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setSaving("");
    }
  };

  const patchList = (key, list) => setContent((c) => ({ ...c, [key]: list }));
  const patchItem = (key, id, patch) =>
    setContent((c) => ({ ...c, [key]: c[key].map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const removeItem = (key, id) => setContent((c) => ({ ...c, [key]: c[key].filter((it) => it.id !== id) }));
  const patchSettings = (patch) => setContent((c) => ({ ...c, settings: { ...c.settings, ...patch } }));

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated. Use it next time you sign in.");
      setPw({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  if (!content) {
    return (
      <div className="grid min-h-screen place-items-center bg-lavender-50" data-testid="admin-dashboard-loading">
        <Loader2 className="animate-spin text-lavender-600" size={28} />
      </div>
    );
  }

  const s = content.settings;

  return (
    <div className="min-h-screen bg-lavender-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-lavender-700/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="Pawtastic Cuts" className="h-8 w-auto" />
            <span className="hidden rounded-full bg-lavender-100 px-3 py-1 text-xs font-semibold text-lavender-800 sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" data-testid="admin-view-site-link" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-stone-600 transition-colors hover:bg-lavender-100 hover:text-lavender-800">
              <ExternalLink size={14} /> View site
            </a>
            <span className="hidden text-xs text-stone-400 md:inline">{user?.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/admin"); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-stone-700"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <Tabs defaultValue="bookings">
          <TabsList className="flex h-auto flex-wrap justify-center gap-1 rounded-3xl border border-lavender-700/10 bg-white p-1.5 sm:rounded-full" data-testid="admin-tabs">
            {[
              ["bookings", "Bookings"],
              ["waivers", "Waivers"],
              ["services", "Services"],
              ["gallery", "Grass Wall Slideshow"],
              ["transformations", "Before & After"],
              ["reviews", "Reviews"],
              ["groomers", "Groomers"],
              ["events", "Events"],
              ["settings", "Settings"],
            ].map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-lavender-700 data-[state=active]:text-white">
                {label}
                {v === "bookings" && newBookings > 0 && (
                  <span data-testid="new-bookings-badge" className="ml-2 inline-grid min-w-5 place-items-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                    {newBookings}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            <BookingsTab />
          </TabsContent>

          <TabsContent value="waivers" className="mt-8">
            <WaiversTab />
          </TabsContent>

          <TabsContent value="services" className="mt-8">
            <p className="text-sm text-stone-500">Edit names, descriptions and prices. Turn prices on/off site-wide in Settings.</p>
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {content.services.map((svc, i) => (
                <Card key={svc.id} testid={`service-editor-${i}`} onDelete={() => patchList("services", content.services.filter((x) => x.id !== svc.id))}>
                  <div className="grid gap-4 sm:grid-cols-[1fr_130px]">
                    <div>
                      <Label>Name</Label>
                      <Input className={fieldCls} value={svc.name} onChange={(e) => patchItem("services", svc.id, { name: e.target.value })} data-testid={`service-name-input-${i}`} />
                    </div>
                    <div>
                      <Label>Price</Label>
                      <Input className={fieldCls} value={svc.price} onChange={(e) => patchItem("services", svc.id, { price: e.target.value })} data-testid={`service-price-input-${i}`} placeholder="from $65" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Description</Label>
                    <Textarea className={fieldCls} rows={2} value={svc.description} onChange={(e) => patchItem("services", svc.id, { description: e.target.value })} data-testid={`service-desc-input-${i}`} />
                  </div>
                  <label className="mt-4 flex items-center gap-2.5 text-sm text-stone-600">
                    <Switch checked={svc.featured} onCheckedChange={(v) => patchItem("services", svc.id, { featured: v })} data-testid={`service-featured-switch-${i}`} />
                    Featured (big lavender card)
                  </label>
                </Card>
              ))}
            </div>
            <button
              onClick={() => patchList("services", [...content.services, { id: uid(), name: "New Service", description: "", price: "", featured: false }])}
              data-testid="add-service-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-lavender-300 px-6 py-3 text-sm font-semibold text-lavender-700 transition-colors hover:border-lavender-500 hover:bg-lavender-100"
            >
              <Plus size={16} /> Add service
            </button>
            <SaveBar section="services" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="gallery" className="mt-8">
            <p className="text-sm text-stone-500">These photos fade one after another in the square frame at the top of the homepage. Upload from your computer, then crop the square so your pup is framed just right.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {content.slides.map((sl, i) => (
                <Card key={sl.id} testid={`slide-editor-${i}`} onDelete={() => removeItem("slides", sl.id)}>
                  <div className="mb-4 aspect-square overflow-hidden rounded-2xl bg-lavender-100">
                    {sl.imageUrl && <img src={sl.imageUrl} alt="" className="h-full w-full object-cover object-center" data-testid={`slide-preview-${i}`} />}
                  </div>
                  <p className="mb-2 text-xs text-stone-400">Shown as a square on the site. Use Crop to choose exactly what visitors see.</p>
                  <Label>Image URL</Label>
                  <Input className={fieldCls} value={sl.imageUrl} onChange={(e) => patchItem("slides", sl.id, { imageUrl: e.target.value })} data-testid={`slide-url-input-${i}`} placeholder="https://…" />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <UploadButton
                      testid={`slide-upload-${i}`}
                      onUploaded={(url) => { patchItem("slides", sl.id, { imageUrl: url }); setCropping({ id: sl.id, src: url }); }}
                    />
                    <button
                      type="button"
                      onClick={() => setCropping({ id: sl.id, src: sl.imageUrl })}
                      disabled={!sl.imageUrl}
                      data-testid={`slide-crop-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-lavender-300 px-4 py-2 text-xs font-semibold text-lavender-800 transition-colors hover:bg-lavender-100 disabled:opacity-50"
                    >
                      <Crop size={13} /> Crop
                    </button>
                  </div>
                  <div className="mt-3">
                    <Label>Caption</Label>
                    <Input className={fieldCls} value={sl.caption} onChange={(e) => patchItem("slides", sl.id, { caption: e.target.value })} data-testid={`slide-caption-input-${i}`} placeholder="Fresh off the table" />
                  </div>
                </Card>
              ))}
            </div>
            <CropDialog
              open={cropping !== null}
              src={cropping?.src || ""}
              onClose={() => setCropping(null)}
              onCropped={(url) => patchItem("slides", cropping.id, { imageUrl: url })}
            />
            <button
              onClick={() => patchList("slides", [...content.slides, { id: uid(), imageUrl: "", caption: "" }])}
              data-testid="add-slide-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-lavender-300 px-6 py-3 text-sm font-semibold text-lavender-700 transition-colors hover:border-lavender-500 hover:bg-lavender-100"
            >
              <ImagePlus size={16} /> Add photo
            </button>
            <SaveBar section="gallery" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="transformations" className="mt-8">
            <TransformationsTab content={content} patchItem={patchItem} removeItem={removeItem} patchList={patchList} Card={Card} />
            <SaveBar section="transformations" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="reviews" className="mt-8">
            <ReviewsTab content={content} patchItem={patchItem} removeItem={removeItem} patchList={patchList} Card={Card} />
            <SaveBar section="reviews" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="groomers" className="mt-8">
            <div className="grid gap-5 lg:grid-cols-2">
              {content.groomers.map((g, i) => (
                <Card key={g.id} testid={`groomer-editor-${i}`} onDelete={() => removeItem("groomers", g.id)}>
                  <div className="flex gap-5">
                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-lavender-100">
                      {g.imageUrl && <img src={g.imageUrl} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="grid flex-1 gap-3">
                      <div>
                        <Label>Name</Label>
                        <Input className={fieldCls} value={g.name} onChange={(e) => patchItem("groomers", g.id, { name: e.target.value })} data-testid={`groomer-name-input-${i}`} />
                      </div>
                      <div>
                        <Label>Title</Label>
                        <Input className={fieldCls} value={g.role} onChange={(e) => patchItem("groomers", g.id, { role: e.target.value })} data-testid={`groomer-role-input-${i}`} placeholder="Owner & Master Groomer" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Photo URL</Label>
                    <Input className={fieldCls} value={g.imageUrl} onChange={(e) => patchItem("groomers", g.id, { imageUrl: e.target.value })} data-testid={`groomer-photo-input-${i}`} placeholder="https://…" />
                    <div className="mt-2">
                      <UploadButton testid={`groomer-upload-${i}`} onUploaded={(url) => patchItem("groomers", g.id, { imageUrl: url })} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Bio</Label>
                    <Textarea className={fieldCls} rows={3} value={g.bio} onChange={(e) => patchItem("groomers", g.id, { bio: e.target.value })} data-testid={`groomer-bio-input-${i}`} />
                  </div>
                </Card>
              ))}
            </div>
            <button
              onClick={() => patchList("groomers", [...content.groomers, { id: uid(), name: "", role: "", bio: "", imageUrl: "" }])}
              data-testid="add-groomer-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-lavender-300 px-6 py-3 text-sm font-semibold text-lavender-700 transition-colors hover:border-lavender-500 hover:bg-lavender-100"
            >
              <Plus size={16} /> Add groomer
            </button>
            <SaveBar section="groomers" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="events" className="mt-8">
            <p className="text-sm text-stone-500">Active events show in the ribbon at the top of the site and in the Happenings section.</p>
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {content.events.map((ev, i) => (
                <Card key={ev.id} testid={`event-editor-${i}`} onDelete={() => removeItem("events", ev.id)}>
                  <div className="grid gap-4 sm:grid-cols-[1fr_150px]">
                    <div>
                      <Label>Title</Label>
                      <Input className={fieldCls} value={ev.title} onChange={(e) => patchItem("events", ev.id, { title: e.target.value })} data-testid={`event-title-input-${i}`} />
                    </div>
                    <div>
                      <Label>Date label</Label>
                      <Input className={fieldCls} value={ev.date} onChange={(e) => patchItem("events", ev.id, { date: e.target.value })} data-testid={`event-date-input-${i}`} placeholder="Sat, Aug 9" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Details</Label>
                    <Textarea className={fieldCls} rows={2} value={ev.description} onChange={(e) => patchItem("events", ev.id, { description: e.target.value })} data-testid={`event-desc-input-${i}`} />
                  </div>
                  <label className="mt-4 flex items-center gap-2.5 text-sm text-stone-600">
                    <Switch checked={ev.active} onCheckedChange={(v) => patchItem("events", ev.id, { active: v })} data-testid={`event-active-switch-${i}`} />
                    Show on the website
                  </label>
                </Card>
              ))}
            </div>
            <button
              onClick={() => patchList("events", [...content.events, { id: uid(), title: "", date: "", description: "", active: true }])}
              data-testid="add-event-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-lavender-300 px-6 py-3 text-sm font-semibold text-lavender-700 transition-colors hover:border-lavender-500 hover:bg-lavender-100"
            >
              <Plus size={16} /> Add event / announcement
            </button>
            <SaveBar section="events" saving={saving} onSave={save} />
          </TabsContent>

          <TabsContent value="settings" className="mt-8">
            <div className="grid gap-5 lg:grid-cols-2">
              <Card testid="settings-general-card">
                <label className="flex items-center justify-between gap-4 text-sm font-medium text-stone-700">
                  Show prices on the website
                  <Switch checked={s.showPrices} onCheckedChange={(v) => patchSettings({ showPrices: v })} data-testid="show-prices-switch" />
                </label>
                <label className="mt-4 flex items-center justify-between gap-4 border-t border-lavender-700/10 pt-4 text-sm font-medium text-stone-700">
                  Show the Groomers section &amp; tab
                  <Switch checked={s.showGroomers !== false} onCheckedChange={(v) => patchSettings({ showGroomers: v })} data-testid="show-groomers-switch" />
                </label>
                <label className="mt-4 flex items-center justify-between gap-4 border-t border-lavender-700/10 pt-4 text-sm font-medium text-stone-700">
                  Show the Reviews page &amp; tab
                  <Switch checked={s.showReviews !== false} onCheckedChange={(v) => patchSettings({ showReviews: v })} data-testid="show-reviews-switch" />
                </label>
                <div className="mt-4 border-t border-lavender-700/10 pt-4" data-testid="paw-intensity-control">
                  <div className="flex items-center justify-between gap-4 text-sm font-medium text-stone-700">
                    <span>Background paw prints<span className="block text-xs font-normal text-stone-400">Big light-purple paws behind the whole site. Slide left for lighter, right for bolder. 0 hides them.</span></span>
                    <span data-testid="paw-intensity-value" className="rounded-md bg-lavender-100 px-2.5 py-1 text-xs font-bold text-lavender-800">{s.pawIntensity ?? 50}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Lighter</span>
                    <Slider min={0} max={100} step={5} value={[s.pawIntensity ?? 50]} onValueChange={([v]) => patchSettings({ pawIntensity: v })} data-testid="paw-intensity-slider" className="flex-1" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Bolder</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-4 text-sm font-medium text-stone-700">
                    <span>Paw print color<span className="block text-xs font-normal text-stone-400">{s.holidayEnabled === true ? "While a holiday theme is on, the paws use the theme's color. Turn the theme off to use this color." : "Pick a preset for seasonal themes or choose any custom color."}</span></span>
                    <label className="flex items-center gap-2 text-xs font-semibold text-stone-500">
                      Custom
                      <input
                        type="color"
                        value={normalizePawColor(s.pawColor)}
                        onChange={(e) => patchSettings({ pawColor: e.target.value.toUpperCase() })}
                        data-testid="paw-color-input"
                        aria-label="Custom paw print color"
                        className="h-8 w-10 cursor-pointer rounded-md border border-lavender-700/15 bg-white p-0.5"
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="paw-color-presets">
                    {PAW_PRESETS.map((p) => {
                      const active = normalizePawColor(s.pawColor).toUpperCase() === p.color.toUpperCase();
                      return (
                        <button
                          key={p.color}
                          type="button"
                          onClick={() => patchSettings({ pawColor: p.color })}
                          data-testid={`paw-color-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                          aria-pressed={active}
                          title={p.name}
                          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-lavender-700 bg-lavender-700 text-white" : "border-lavender-700/15 bg-white text-stone-700 hover:border-lavender-400"}`}
                        >
                          <span className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: p.color }} />
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 h-16 overflow-hidden rounded-xl border border-lavender-700/10 bg-white" data-testid="paw-intensity-preview">
                    <div className="paw-preview h-full w-full" style={pawStyle(s)} data-paw-color={effectivePawColor(s)} />
                  </div>
                </div>
                <label className="mt-4 flex items-center justify-between gap-4 border-t border-lavender-700/10 pt-4 text-sm font-medium text-stone-700">
                  <span>Morning daily digest email<span className="block text-xs font-normal text-stone-400">Every day at 7am Eastern we email the shop a summary of yesterday's booking requests and signed waivers.</span></span>
                  <Switch checked={s.dailyDigest !== false} onCheckedChange={(v) => patchSettings({ dailyDigest: v })} data-testid="daily-digest-switch" />
                </label>
                <label className="mt-4 flex items-center justify-between gap-4 border-t border-lavender-700/10 pt-4 text-sm font-medium text-stone-700">
                  <span>Let visitors flip through the homepage slideshow<span className="block text-xs font-normal text-stone-400">Adds subtle arrows and swipe. Photos still fade on their own, and the order reshuffles automatically every day at 4am.</span></span>
                  <Switch checked={s.slideshowControls !== false} onCheckedChange={(v) => patchSettings({ slideshowControls: v })} data-testid="slideshow-controls-switch" />
                </label>
                <label className="mt-4 flex items-center justify-between gap-4 border-t border-lavender-700/10 pt-4 text-sm font-medium text-stone-700">
                  <span>Show Before &amp; After on the homepage<span className="block text-xs font-normal text-stone-400">Off by default. Edit the photos in the Before &amp; After tab.</span></span>
                  <Switch checked={s.showBeforeAfter === true} onCheckedChange={(v) => patchSettings({ showBeforeAfter: v })} data-testid="show-before-after-switch" />
                </label>
                <div className="mt-5 space-y-4">
                  <div>
                    <Label>Facebook page URL</Label>
                    <Input className={fieldCls} value={s.facebookUrl} onChange={(e) => patchSettings({ facebookUrl: e.target.value })} data-testid="facebook-url-input" placeholder="https://www.facebook.com/yourpage" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Phone</Label>
                      <Input className={fieldCls} value={s.phone} onChange={(e) => patchSettings({ phone: e.target.value })} data-testid="phone-input" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input className={fieldCls} value={s.email} onChange={(e) => patchSettings({ email: e.target.value })} data-testid="email-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input className={fieldCls} value={s.address} onChange={(e) => patchSettings({ address: e.target.value })} data-testid="address-input" />
                  </div>
                  <div>
                    <Label>Hours</Label>
                    <Input className={fieldCls} value={s.hours} onChange={(e) => patchSettings({ hours: e.target.value })} data-testid="hours-input" />
                  </div>
                  <div>
                    <Label>Policy note</Label>
                    <Textarea className={fieldCls} rows={2} value={s.policy} onChange={(e) => patchSettings({ policy: e.target.value })} data-testid="policy-input" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <Label>Grooming waiver text</Label>
                      <button
                        type="button"
                        data-testid="waiver-reset-button"
                        onClick={async () => {
                          if (!window.confirm("Replace the current waiver text with the official Pawtastic Cuts, LLC Policy Agreement?")) return;
                          try {
                            const { data } = await api.post("/admin/content/waiver/reset");
                            patchSettings({ waiverText: data.waiverText });
                            toast.success("Waiver text restored to the official agreement");
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail));
                          }
                        }}
                        className="text-xs font-semibold text-lavender-700 underline underline-offset-2 hover:text-lavender-900"
                      >
                        Restore official waiver
                      </button>
                    </div>
                    <p className="mt-0.5 text-xs text-stone-500">Shown in the appointment form and on the Grooming Waiver page (QR code in the Waivers tab). Blank lines create paragraphs.</p>
                    <Textarea className={`${fieldCls} font-mono text-xs`} rows={10} value={s.waiverText || ""} onChange={(e) => patchSettings({ waiverText: e.target.value })} data-testid="waiver-text-input" />
                  </div>
                </div>
                <SaveBar section="settings" saving={saving} onSave={save} />
              </Card>

              <div className="space-y-5">
              <HolidayCard settings={s} patchSettings={patchSettings} Card={Card} />
              <ExitPopupCard settings={s} patchSettings={patchSettings} Card={Card} />
              <Card testid="settings-password-card">
                <h3 className="font-serif text-xl text-stone-900">Change admin password</h3>
                <p className="mt-1 text-sm text-stone-500">Signed in as {user?.email}</p>
                <form onSubmit={changePassword} className="mt-5 space-y-4">
                  <div>
                    <Label>Current password</Label>
                    <Input className={fieldCls} type="password" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="current-password-input" autoComplete="current-password" />
                  </div>
                  <div>
                    <Label>New password</Label>
                    <Input className={fieldCls} type="password" required minLength={6} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="new-password-input" autoComplete="new-password" />
                  </div>
                  <Button type="submit" disabled={pwBusy} data-testid="change-password-button" className="rounded-full bg-stone-900 px-6 font-semibold hover:bg-stone-700">
                    {pwBusy ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
                  </Button>
                </form>
              </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
